import { useEffect, useRef } from "react";
import type { LiveInfo } from "@tih/shared";

type Message =
  | { type: "live:snapshot"; data: LiveInfo }
  | { type: "live:update"; data: LiveInfo };

const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

function wsUrl(path: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}${path}`;
}

/**
 * Subscribe to live stream updates. The callback fires for both the
 * initial snapshot the server sends on connect and every subsequent push.
 *
 * Self-heals across network drops with exponential backoff.
 */
export function useLiveSocket(onInfo: (info: LiveInfo) => void) {
  const cbRef = useRef(onInfo);
  cbRef.current = onInfo;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let backoff = MIN_BACKOFF_MS;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    function connect() {
      if (stopped) return;
      try {
        ws = new WebSocket(wsUrl("/ws/live"));
      } catch {
        scheduleReconnect();
        return;
      }

      ws.addEventListener("open", () => {
        backoff = MIN_BACKOFF_MS;
      });

      ws.addEventListener("message", (event) => {
        try {
          const msg = JSON.parse(String(event.data)) as Message;
          if (msg && (msg.type === "live:update" || msg.type === "live:snapshot")) {
            cbRef.current(msg.data);
          }
        } catch {
          /* ignore garbage frames */
        }
      });

      ws.addEventListener("close", scheduleReconnect);
      ws.addEventListener("error", () => {
        // close will fire too; let scheduling happen there.
        try {
          ws?.close();
        } catch {
          /* noop */
        }
      });
    }

    function scheduleReconnect() {
      if (stopped) return;
      if (reconnectTimer) return;
      const delay = backoff;
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    }

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        ws?.close();
      } catch {
        /* noop */
      }
    };
  }, []);
}
